import { useState } from "react";
import { api } from "@/lib/api";
import { validate } from "@/lib/passwordPolicy";
import { PasswordStrengthMeter } from "@/components/security/PasswordStrengthMeter";
import { KeyRound, Loader2, Check, AlertTriangle, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/util";

/**
 * Self-service password change section. Rendered inside /settings for every
 * authenticated user. Enforces the same 14-char strict policy the backend does
 * (14 chars + upper/lower/digit/symbol + no reuse of the last 5).
 */
export default function PasswordSection() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showNext, setShowNext] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  const policy = validate(next);
  const matches = next && next === confirm;
  const differsFromCurrent = next && next !== current;
  const canSubmit = current.length > 0 && policy.ok && matches && differsFromCurrent && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true); setErr(""); setOk(false);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      setOk(true);
      setCurrent(""); setNext(""); setConfirm("");
      toast.success("Contraseña actualizada");
    } catch (ex) {
      const detail = ex?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail :
             "No se pudo cambiar la contraseña");
    } finally { setBusy(false); }
  };

  return (
    <section className="border border-border bg-card/60 p-6" data-testid="settings-password">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 border border-primary/60 bg-primary/10 flex items-center justify-center">
          <KeyRound className="w-4 h-4 text-primary" />
        </div>
        <div className="flex-1">
          <div className="overline">Cuenta</div>
          <h2 className="font-display text-2xl font-medium">Cambiar contraseña</h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            Rota tu contraseña respetando la política corporativa. Requiere 14+
            caracteres con mayúsculas, minúsculas, dígitos y un símbolo, y no
            puede coincidir con ninguna de las últimas 5 que hayas usado.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-6"
            data-testid="password-change-form">
        <div className="space-y-4">
          <label className="block">
            <div className="overline mb-1">Contraseña actual</div>
            <input type="password" value={current} autoComplete="current-password"
                   onChange={(e) => setCurrent(e.target.value)}
                   className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none"
                   data-testid="password-current-input" required />
          </label>

          <label className="block">
            <div className="overline mb-1 flex items-center justify-between">
              <span>Nueva contraseña</span>
              <button type="button"
                      onClick={() => setShowNext(v => !v)}
                      className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary inline-flex items-center gap-1"
                      data-testid="password-toggle-visibility">
                {showNext ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                {showNext ? "Ocultar" : "Ver"}
              </button>
            </div>
            <input type={showNext ? "text" : "password"} value={next}
                   autoComplete="new-password"
                   onChange={(e) => setNext(e.target.value)}
                   className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none"
                   data-testid="password-new-input" required />
          </label>

          <label className="block">
            <div className="overline mb-1">Confirmar nueva contraseña</div>
            <input type={showNext ? "text" : "password"} value={confirm}
                   autoComplete="new-password"
                   onChange={(e) => setConfirm(e.target.value)}
                   className={cn("w-full bg-background border px-3 py-2 text-sm font-mono outline-none",
                     confirm && !matches ? "border-sev-critical focus:border-sev-critical"
                                         : "border-border focus:border-primary")}
                   data-testid="password-confirm-input" required />
            {confirm && !matches && (
              <div className="mt-1 text-[10px] font-mono text-sev-critical" data-testid="password-mismatch">
                Las contraseñas no coinciden
              </div>
            )}
          </label>

          {err && (
            <div className="border border-sev-critical/50 bg-sev-critical/10 text-sev-critical p-2 text-xs font-mono flex items-start gap-2"
                 data-testid="password-change-error">
              <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
              <span>{err}</span>
            </div>
          )}
          {ok && (
            <div className="border border-sev-low/50 bg-sev-low/10 text-sev-low p-2 text-xs font-mono flex items-center gap-2"
                 data-testid="password-change-success">
              <Check className="w-3 h-3" /> Contraseña actualizada
            </div>
          )}

          <button type="submit" disabled={!canSubmit}
                  className="bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="password-change-submit">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <KeyRound className="w-3 h-3" />}
            Actualizar contraseña
          </button>
        </div>

        <div className="border border-border bg-background/40 p-4">
          <div className="overline mb-2">Política y fortaleza en vivo</div>
          <PasswordStrengthMeter password={next} showRulesAlways />
          <div className="mt-4 text-[10px] font-mono text-muted-foreground border-t border-border pt-3">
            Tras cambiar la contraseña, tu sesión actual sigue activa, pero
            cualquier nuevo login exigirá la nueva. Si fallas 3 intentos
            seguidos, la cuenta se bloqueará temporalmente (5 min → 30 min →
            permanente).
          </div>
        </div>
      </form>
    </section>
  );
}
